import React, { useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowForward as BackIcon,
  Home as HomeIcon,
  ProductionQuantityLimits as MissingProductIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";

const BRAND = "#ef4056";


const QUICK_LINKS = [
  { text: "صفحه اصلی", href: "/" },
  { text: "پاسخ سوالات متداول", href: "/faq" },
  { text: "نحوه ثبت سفارش", href: "/condition" },
  { text: "پیگیری سفارش‌ها", href: "/invoices" },
];

/**
 * NotFoundPage — Professional 404 page.
 *
 * Design decisions:
 * - The "404" code is kept but demoted to a small status Chip. Keeping the
 *   code helps users recognise the error and report it to support; making it
 *   subtle (instead of a giant badge) keeps the page professional.
 * - Animation is purposeful and calm: one entrance cascade + a gentle float
 *   on the illustration + soft pulse rings. All animations are disabled for
 *   users who prefer reduced motion.
 */
export default function NotFoundPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "صفحه پیدا نشد | زومیو";
  }, []);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <Box
      sx={{
        py: { xs: 5, md: 9 },
        px: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        direction: "rtl",
        position: "relative",
        overflow: "hidden",
        // Soft brand-tinted backdrop decoration
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `radial-gradient(480px 240px at 50% 0%, ${BRAND}14, transparent 70%)`,
          pointerEvents: "none",
        },
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(22px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "@keyframes floatSoft": {
          "0%, 100%": { transform: "translateY(0px)" },
          "50%": { transform: "translateY(-9px)" },
        },
        "@keyframes pulseRing": {
          "0%": { opacity: 0.55, transform: "scale(0.92)" },
          "70%": { opacity: 0, transform: "scale(1.18)" },
          "100%": { opacity: 0, transform: "scale(1.18)" },
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3.5, sm: 5 },
            borderRadius: { xs: 4, sm: 5 },
            textAlign: "center",
            background: "rgba(255, 255, 255, 0.92)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(226, 232, 240, 0.9)",
            boxShadow: "0 20px 45px rgba(15, 23, 42, 0.06)",
            animation: "fadeInUp 0.55s cubic-bezier(0.16, 1, 0.3, 1) both",
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {/* Status code — kept small and professional */}
          <Chip
            label="خطای 404"
            size="small"
            sx={{
              mb: 3,
              fontWeight: 700,
              color: BRAND,
              bgcolor: `${BRAND}12`,
              border: `1px solid ${BRAND}30`,
              animation: "fadeInUp 0.55s 0.08s cubic-bezier(0.16, 1, 0.3, 1) both",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />

          {/* Calm animated illustration */}
          <Box
            sx={{
              position: "relative",
              width: { xs: 132, sm: 156 },
              height: { xs: 132, sm: 156 },
              mx: "auto",
              mb: { xs: 3, sm: 3.5 },
              animation: "fadeInUp 0.55s 0.14s cubic-bezier(0.16, 1, 0.3, 1) both",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            {/* Soft pulse rings */}
            {[0, 1].map((i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  border: `1.5px solid ${BRAND}45`,
                  animation: `pulseRing 2.8s ease-out ${i * 1.4}s infinite`,
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                    opacity: 0.25,
                  },
                }}
              />
            ))}
            {/* Floating medallion */}
            <Box
              sx={{
                position: "absolute",
                inset: 14,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${BRAND} 0%, #c81e3a 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                boxShadow: `0 16px 32px ${BRAND}45`,
                animation: "floatSoft 5s ease-in-out infinite",
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
              }}
            >
              <MissingProductIcon sx={{ fontSize: { xs: 46, sm: 56 } }} />
            </Box>
          </Box>

          {/* Heading */}
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 800,
              color: "#0f172a",
              mb: 1.5,
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              animation: "fadeInUp 0.55s 0.2s cubic-bezier(0.16, 1, 0.3, 1) both",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            صفحه یا محصول مورد نظر پیدا نشد
          </Typography>

          {/* Subtext */}
          <Typography
            variant="body2"
            sx={{
              mb: 3.5,
              maxWidth: 440,
              mx: "auto",
              lineHeight: 2,
              color: "#64748b",
              animation: "fadeInUp 0.55s 0.26s cubic-bezier(0.16, 1, 0.3, 1) both",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            ممکن است محصول ناموجود شده یا نشانی صفحه تغییر کرده باشد. از
            دکمه‌های زیر برای ادامه خرید استفاده کنید.
          </Typography>

          {/* Actions */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="center"
            sx={{
              animation: "fadeInUp 0.55s 0.32s cubic-bezier(0.16, 1, 0.3, 1) both",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <Button
              variant="contained"
              disableElevation
              startIcon={<BackIcon sx={{ ml: 1, mr: -0.5 }} />}
              onClick={handleGoBack}
              sx={{
                py: 1.2,
                px: 3.5,
                borderRadius: 2.5,
                fontSize: "0.95rem",
                fontWeight: 700,
                minHeight: 46,
                backgroundColor: BRAND,
                "&:hover": { backgroundColor: "#d73247" },
                "&:active": { transform: "scale(0.98)" },
              }}
            >
              بازگشت به صفحه قبل
            </Button>

            <Button
              variant="outlined"
              startIcon={<HomeIcon sx={{ ml: 1, mr: -0.5 }} />}
              onClick={() => navigate("/")}
              sx={{
                py: 1.2,
                px: 3.5,
                borderRadius: 2.5,
                fontSize: "0.95rem",
                fontWeight: 700,
                minHeight: 46,
                borderColor: "#cbd5e1",
                color: "#334155",
                "&:hover": {
                  borderColor: BRAND,
                  backgroundColor: `${BRAND}0a`,
                },
                "&:active": { transform: "scale(0.98)" },
              }}
            >
              صفحه اصلی فروشگاه
            </Button>
          </Stack>

          {/* Quick links */}
          <Divider sx={{ my: 3.5, borderColor: "grey.200" }} />
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
            sx={{
              animation: "fadeInUp 0.55s 0.38s cubic-bezier(0.16, 1, 0.3, 1) both",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.href + link.text}
                component={RouterLink}
                to={link.href}
                underline="hover"
                color="text.secondary"
                sx={{ fontSize: "0.85rem", fontWeight: 600 }}
              >
                {link.text}
              </Link>
            ))}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
